import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import { formatINR } from "@/lib/utils";

const EMPTY = {
  name: "", tagline: "", audience: "",
  credits: 100, price_inr: 10000,
  features: [], active: true, highlighted: false,
};

export default function AdminPlansPage() {
  const [plans, setPlans] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [featInput, setFeatInput] = useState("");

  const load = () => api.get("/plans?only_active=false").then(({ data }) => setPlans(data));
  useEffect(() => { load(); }, []);

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name, tagline: p.tagline, audience: p.audience,
      credits: p.credits, price_inr: p.price_inr,
      features: p.features || [], active: p.active, highlighted: p.highlighted,
    });
  };

  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/plans/${editing}`, form);
        toast.success("Plan updated.");
      } else {
        await api.post("/admin/plans", form);
        toast.success("Plan created.");
      }
      cancel(); await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this plan?")) return;
    await api.delete(`/admin/plans/${id}`);
    await load();
  };

  const addFeature = () => {
    if (!featInput.trim()) return;
    setForm({ ...form, features: [...form.features, featInput.trim()] });
    setFeatInput("");
  };

  if (!plans) return <LoadingScreen />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-3" data-testid="admin-plans-list">
        {plans.map((p) => (
          <div key={p.id} className={`sqit-card p-5 ${p.highlighted ? "border-[#0B6936]" : ""}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-lg font-semibold text-gray-900">{p.name}</h3>
                  {p.highlighted && <span className="pill bg-[#0B6936] text-white border-[#538135]/30 text-[10px]">POPULAR</span>}
                  {!p.active && <span className="pill bg-gray-100 text-gray-600 border-gray-200 text-[10px]">INACTIVE</span>}
                </div>
                <p className="text-xs text-gray-500">{p.tagline}</p>
                <div className="mt-2 text-sm text-gray-700">
                  <strong>{p.credits} credits</strong> · {formatINR(p.price_inr)}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => startEdit(p)} className="text-xs px-3 py-1.5 border rounded-md hover:bg-gray-50">Edit</button>
                <button onClick={() => remove(p.id)} className="text-xs px-3 py-1.5 border rounded-md text-red-600 hover:bg-red-50">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="sqit-card p-6" data-testid="admin-plan-form">
        <h3 className="font-display text-lg font-semibold text-gray-900">
          {editing ? "Edit plan" : "New plan"}
        </h3>
        <div className="mt-4 space-y-3">
          {[
            ["name", "Name"], ["tagline", "Tagline"], ["audience", "Audience"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="text-xs font-semibold text-gray-700">{label}</label>
              <input
                value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
              />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Credits</label>
              <input type="number" min={1} value={form.credits}
                onChange={(e) => setForm({ ...form, credits: Number(e.target.value) })}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Price (INR)</label>
              <input type="number" min={0} value={form.price_inr}
                onChange={(e) => setForm({ ...form, price_inr: Number(e.target.value) })}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700">Features</label>
            <div className="flex gap-2 mt-1">
              <input value={featInput} onChange={(e) => setFeatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm" />
              <button onClick={addFeature} className="px-3 py-2 border rounded-md text-sm">Add</button>
            </div>
            <ul className="mt-2 space-y-1">
              {form.features.map((f, i) => (
                <li key={i} className="text-xs bg-gray-50 rounded px-2 py-1 flex justify-between">
                  {f}
                  <button onClick={() => setForm({ ...form, features: form.features.filter((_, j) => j !== i) })}
                    className="text-red-600 ml-2">×</button>
                </li>
              ))}
            </ul>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.highlighted} onChange={(e) => setForm({ ...form, highlighted: e.target.checked })} />
            Mark as highlighted / most popular
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Active
          </label>
          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={saving || !form.name}
              className="flex-1 bg-[#0B6936] text-white hover:bg-[#09582D] rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Save" : "Create"}
            </button>
            {editing && (
              <button onClick={cancel} className="px-4 py-2 border rounded-md text-sm">Cancel</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
